/**
 * Comenzi — panoul de lucru al restaurantului.
 *
 * Ce trebuie să se întâmple în primele secunde după ce sună alarma: cineva vede comanda, o acceptă
 * și bucătăria începe. De aceea pagina este construită în jurul cozii de lucru, nu al unui tabel
 * de raportare:
 *
 *   • **Live**: o comandă nouă apare singură (Server-Sent Events, cu revenire pe citire periodică
 *     dacă rețeaua cade) și se aude un sunet scurt — nimeni nu trebuie să reîncarce pagina;
 *   • **Într-un singur loc**: masa sau adresa, produsele, observațiile și ora promisă;
 *   • **Următorul pas e evident**: butonul principal este exact tranziția validă (Acceptă → În
 *     pregătire → Gata → Finalizată), iar refuzul cere un motiv, pentru că motivul ajunge la client;
 *   • **Setările sunt la un click**: prag de comandă minimă, taxă de livrare, timpi de preparare —
 *     exact lucrurile pe care le schimbi în timpul serviciului;
 *   • **Bon de bucătărie** printabil, pentru bucătăriile care încă lucrează pe hârtie.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DeliveryZoneMap, type ZoneDraft } from '../components/DeliveryZoneMap.js';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Icon,
  InlineAlert,
  Input,
  Modal,
  PageHeader,
  Select,
  Skeleton,
  Switch,
  Textarea,
  useToast,
} from '../components/ui.js';
import { api } from '../lib/api.js';
import { formatCurrency, formatDateTime, useResource } from '../lib/hooks.js';
import { errorMessage, useSession } from '../lib/session.js';

type OrderStatus = 'pending' | 'accepted' | 'preparing' | 'ready' | 'completed' | 'rejected' | 'cancelled';
type OrderChannel = 'dine_in' | 'takeaway' | 'delivery';
type Action = 'accept' | 'start_preparing' | 'mark_ready' | 'complete' | 'reject' | 'cancel';

interface OrderItem {
  product_name: string;
  variant_name: string | null;
  quantity: number;
  unit_price_cents: number;
  line_total_cents: number;
  note: string | null;
}

interface Order {
  id: string;
  code: string;
  status: OrderStatus;
  channel: OrderChannel;
  table_label: string | null;
  customer_name: string | null;
  customer_phone: string;
  delivery_address: string | null;
  delivery_city: string | null;
  customer_note: string | null;
  delivery_notes: string | null;
  currency: string;
  subtotal_cents: number;
  delivery_fee_cents: number;
  total_cents: number;
  eta_minutes: number | null;
  eta_window_minutes: number | null;
  eta_at: string | null;
  placed_at: string;
  accepted_at: string | null;
  ready_at: string | null;
  completed_at: string | null;
  reject_reason: string | null;
  status_history: Array<{ status: OrderStatus; at: string; by: string; note: string | null }> | null;
  items: OrderItem[] | null;
}

interface OrderingSettings {
  restaurantId: string;
  isEnabled: boolean;
  acceptingOrders: boolean;
  dineInEnabled: boolean;
  takeawayEnabled: boolean;
  deliveryEnabled: boolean;
  deliveryFeeCents: number;
  minOrderCents: number;
  freeDeliveryFromCents: number | null;
  prepBufferMinutes: number;
  kitchenQueueMinutes: number;
  deliveryEtaMinutes: number;
  pickupEtaMinutes: number;
  etaWindowMinutes: number;
  deliveryAreas: string | null;
  notes: string | null;
  respectOpeningHours: boolean;
  /** Notificarea pe email la comandă nouă (implicit oprită — vezi migrația 0042). */
  notifyNewOrder: boolean;
  /**
   * Destinatarii scriși de restaurant. Listă goală = anunțul merge la proprietarii și managerii
   * contului, calculați la momentul comenzii (nu îngheață lista în setări).
   */
  notifyEmails: string[];
}

/** Aceeași regulă de validare ca pe server: ce nu trece de aici este ignorat la salvare. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i;
const MAX_NOTIFY_EMAILS = 5;

/** Desparte textul scris de om în adrese, păstrând ordinea și eliminând duplicatele. */
function parseEmails(text: string): { valid: string[]; invalid: string[] } {
  const valid: string[] = [];
  const invalid: string[] = [];
  const seen = new Set<string>();
  for (const piece of text.split(/[,;\n]+/)) {
    const email = piece.trim().toLowerCase();
    if (!email) continue;
    if (!EMAIL_PATTERN.test(email)) {
      invalid.push(piece.trim());
      continue;
    }
    if (seen.has(email)) continue;
    seen.add(email);
    valid.push(email);
  }
  return { valid, invalid };
}

/** Starea de funcționare, așa cum o vede clientul din meniu („deschis acum / deschidem mâine"). */
interface RestaurantState {
  is_open: boolean;
  opens_at: string | null;
  closes_at: string | null;
  next_open_at: string | null;
  nextOpenLabel: string | null;
  has_hours: boolean;
  timezone: string;
}

const STATUS_LABEL: Record<OrderStatus, string> = {
  pending: 'În așteptare',
  accepted: 'Acceptată',
  preparing: 'În pregătire',
  ready: 'Gata',
  completed: 'Finalizată',
  rejected: 'Refuzată',
  cancelled: 'Anulată',
};

const STATUS_TONE: Record<OrderStatus, 'warning' | 'info' | 'success' | 'neutral' | 'danger'> = {
  pending: 'warning',
  accepted: 'info',
  preparing: 'info',
  ready: 'success',
  completed: 'neutral',
  rejected: 'danger',
  cancelled: 'neutral',
};

const CHANNEL_LABEL: Record<OrderChannel, string> = {
  dine_in: 'La masă',
  takeaway: 'Ridicare',
  delivery: 'Livrare',
};

/** Următoarea acțiune care are sens pentru fiecare stare — devine butonul principal al cardului. */
const NEXT_ACTION: Partial<Record<OrderStatus, { action: Action; label: string }>> = {
  pending: { action: 'accept', label: 'Acceptă' },
  accepted: { action: 'start_preparing', label: 'Începe pregătirea' },
  preparing: { action: 'mark_ready', label: 'Gata de predare' },
  ready: { action: 'complete', label: 'Finalizează' },
};

const OPEN_STATUSES: OrderStatus[] = ['pending', 'accepted', 'preparing', 'ready'];

/** Sunet scurt de notificare, generat în browser (fără fișier, fără CDN, fără permisiuni). */
function playChime(): void {
  try {
    const Ctx =
      window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const start = ctx.currentTime;
    [880, 1180].forEach((frequency, index) => {
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = frequency;
      const at = start + index * 0.18;
      gain.gain.setValueAtTime(0.0001, at);
      gain.gain.exponentialRampToValueAtTime(0.2, at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.32);
      oscillator.connect(gain).connect(ctx.destination);
      oscillator.start(at);
      oscillator.stop(at + 0.34);
    });
    window.setTimeout(() => void ctx.close(), 1200);
  } catch {
    /* sunetul este un bonus: dacă browserul îl blochează, comanda rămâne oricum pe ecran */
  }
}

export function OrdersPage() {
  const session = useSession();
  const toast = useToast();
  const [orders, setOrders] = useState<Order[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [restaurantId, setRestaurantId] = useState('');
  const [filter, setFilter] = useState<'open' | OrderStatus | 'all'>('open');
  const [channel, setChannel] = useState<'' | OrderChannel>('');
  const [search, setSearch] = useState('');
  const [sound, setSound] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<Order | null>(null);
  const [reason, setReason] = useState('');
  const [detail, setDetail] = useState<Order | null>(null);
  const [settingsFor, setSettingsFor] = useState<string | null>(null);
  const knownIds = useRef<Set<string>>(new Set());
  const initialised = useRef(false);

  /**
   * Locațiile contului: un cont cu mai multe puncte de lucru vede implicit toate comenzile.
   * `restaurantId` gol înseamnă „toate", iar serverul aplică singur izolarea pe cont.
   */
  const locations = useResource<Array<{ id: string; name: string; restaurant_id: string }>>(() =>
    api.get<Array<{ id: string; name: string; restaurant_id: string }>>('/locations'),
  );
  const restaurants = useMemo(() => {
    const seen = new Map<string, string>();
    for (const location of locations.data ?? []) seen.set(location.restaurant_id, location.name);
    return [...seen].map(([id, name]) => ({ id, name }));
  }, [locations.data]);

  const load = useCallback(
    async (options: { silent?: boolean } = {}) => {
      try {
        const query = new URLSearchParams({ limit: '100' });
        if (restaurantId) query.set('restaurantId', restaurantId);
        if (filter === 'open') query.set('statuses', OPEN_STATUSES.join(','));
        else if (filter !== 'all') query.set('statuses', filter);
        if (channel) query.set('channel', channel);
        if (search.trim()) query.set('search', search.trim());

        const rows = await api.get<Order[]>(`/orders?${query.toString()}`);

        const fresh = rows.filter((order) => !knownIds.current.has(order.id));
        rows.forEach((order) => knownIds.current.add(order.id));
        // Sunetul sună doar pentru comenzi noi, după prima încărcare: la deschiderea paginii nu are
        // rost să alertezi pentru comenzi care erau deja în listă.
        if (initialised.current && sound && fresh.some((order) => order.status === 'pending')) playChime();
        initialised.current = true;

        setOrders(rows);
        setError(null);
      } catch (caught) {
        if (!options.silent) setError(errorMessage(caught));
      }
    },
    [restaurantId, filter, channel, search, sound],
  );

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Fluxul live: serverul anunță fiecare comandă nouă și fiecare schimbare de stare. Dacă legătura
   * cade, `EventSource` se reconectează singur; în plus, lista se recitește la 25 de secunde, ca un
   * panou din bucătărie să nu rămână niciodată mut fiindcă „nu a venit evenimentul".
   */
  useEffect(() => {
    const interval = window.setInterval(() => void load({ silent: true }), 25_000);

    /**
     * „Toate locațiile" înseamnă toate restaurantele contului, pe aceeași conexiune — dar lista de
     * locații se încarcă asincron. Fără această gardă, pagina deschidea fluxul cu un set gol de
     * identificatori, iar serverul răspundea corect cu 422 („fluxul are nevoie de un restaurant"):
     * o eroare în consolă care nu împiedica nimic, dar care poluează orice diagnostic. Fluxul se
     * deschide când există un domeniu real de ascultat, iar efectul se reia automat la sosirea
     * locațiilor.
     */
    const scope = restaurantId
      ? `restaurantId=${restaurantId}`
      : restaurants.length
        ? `restaurantIds=${restaurants.map((restaurant) => restaurant.id).join(',')}`
        : null;
    if (!scope) {
      return () => window.clearInterval(interval);
    }

    let stream: EventSource | null = null;
    try {
      stream = new EventSource(`/api/v1/orders/stream?${scope}`, { withCredentials: true });
      stream.addEventListener('order.created', () => void load({ silent: true }));
      stream.addEventListener('order.status', () => void load({ silent: true }));
    } catch {
      /* dacă SSE nu e disponibil, rămâne reîmprospătarea periodică */
    }
    return () => {
      window.clearInterval(interval);
      stream?.close();
    };
  }, [restaurantId, restaurants, load]);

  const act = async (order: Order, action: Action, reasonText?: string) => {
    setBusy(order.id);
    try {
      await api.post(`/orders/${order.id}/status`, { action, reason: reasonText ?? null });
      toast.success(
        action === 'accept'
          ? `Comanda #${order.code} a fost acceptată`
          : action === 'reject'
            ? `Comanda #${order.code} a fost refuzată`
            : `Comanda #${order.code} a fost actualizată`,
      );
      await load({ silent: true });
    } catch (caught) {
      toast.error('Nu am putut actualiza comanda', errorMessage(caught));
    } finally {
      setBusy(null);
    }
  };

  /**
   * Starea de funcționare („deschis acum / închis, deschidem la …").
   *
   * Are nevoie doar de un restaurant: când filtrezi „toate locațiile", se ia primul. Se recitește
   * la fiecare minut, pentru că se schimbă singură, cu ceasul — nu printr-o acțiune în interfață.
   */
  const [openState, setOpenState] = useState<RestaurantState | null>(null);
  useEffect(() => {
    const id = restaurantId || restaurants[0]?.id;
    if (!id) return;
    let alive = true;
    const loadState = async () => {
      try {
        const next = await api.get<RestaurantState>(`/ordering/state?restaurantId=${id}`);
        if (alive) setOpenState(next);
      } catch {
        /* starea de program este informativă: dacă nu se poate citi, panoul funcționează mai departe */
      }
    };
    void loadState();
    const interval = window.setInterval(() => void loadState(), 60_000);
    return () => {
      alive = false;
      window.clearInterval(interval);
    };
  }, [restaurantId, restaurants]);

  const summary = useMemo(() => {
    const rows = orders ?? [];
    return {
      pending: rows.filter((order) => order.status === 'pending').length,
      working: rows.filter((order) => order.status === 'accepted' || order.status === 'preparing').length,
      ready: rows.filter((order) => order.status === 'ready').length,
      value: rows
        .filter((order) => order.status !== 'rejected' && order.status !== 'cancelled')
        .reduce((sum, order) => sum + order.total_cents, 0),
      currency: rows[0]?.currency ?? 'RON',
    };
  }, [orders]);

  if (!session.can('order.view')) {
    return (
      <Card>
        <EmptyState
          icon={<Icon name="shield" size={26} />}
          title="Fără acces la comenzi"
          description="Rolul tău nu include vizualizarea comenzilor. Cere unui manager să îți acorde dreptul „Comenzi”."
        />
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Comenzi"
        subtitle="Comenzile intră aici în timp real — de la masă, de la ridicare sau de la livrare."
        actions={
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-caption text-ink-600">
              <Switch checked={sound} onChange={setSound} label="Alertă sonoră" />
              Alertă sonoră
            </label>
            {session.can('order.settings') ? (
              <Button variant="ghost" icon={<Icon name="settings" />} onClick={() => setSettingsFor(restaurantId || restaurants[0]?.id || '')}>
                Setări comenzi
              </Button>
            ) : null}
            <Button variant="ghost" icon={<Icon name="trending" />} onClick={() => void load()}>
              Reîncarcă
            </Button>
          </div>
        }
      />

      {error ? (
        <InlineAlert tone="danger" title="Nu am putut încărca comenzile">
          {error}{' '}
          <button type="button" className="underline" onClick={() => void load()}>
            Reîncearcă
          </button>
        </InlineAlert>
      ) : null}

      <section aria-label="Indicatori" className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[
          { label: 'În așteptare', value: String(summary.pending), tone: summary.pending ? 'text-warning' : 'text-ink-950' },
          { label: 'În lucru', value: String(summary.working), tone: 'text-ink-950' },
          { label: 'Gata de predare', value: String(summary.ready), tone: 'text-ink-950' },
          { label: 'Valoare în listă', value: formatCurrency(summary.value, summary.currency), tone: 'text-ink-950' },
        ].map((kpi) => (
          <Card key={kpi.label} className="flex flex-col gap-1">
            <span className="text-caption text-ink-400">{kpi.label}</span>
            <span className={`font-display text-[26px] leading-none nums ${kpi.tone}`}>{kpi.value}</span>
          </Card>
        ))}
      </section>

      <Card className="flex flex-wrap items-center gap-3">
        <Select
          value={restaurantId}
          onChange={(event) => setRestaurantId(event.target.value)}
          className="max-w-[200px]"
          aria-label="Locație"
        >
          <option value="">Toate locațiile</option>
          {restaurants.map((restaurant) => (
            <option key={restaurant.id} value={restaurant.id}>
              {restaurant.name}
            </option>
          ))}
        </Select>
        <Select
          value={filter}
          onChange={(event) => setFilter(event.target.value as typeof filter)}
          className="max-w-[190px]"
          aria-label="Stare"
        >
          <option value="open">Comenzi deschise</option>
          <option value="pending">Doar în așteptare</option>
          <option value="accepted">Acceptate</option>
          <option value="preparing">În pregătire</option>
          <option value="ready">Gata</option>
          <option value="completed">Finalizate</option>
          <option value="rejected">Refuzate</option>
          <option value="all">Toate</option>
        </Select>
        <Select
          value={channel}
          onChange={(event) => setChannel(event.target.value as typeof channel)}
          className="max-w-[170px]"
          aria-label="Canal"
        >
          <option value="">Toate canalele</option>
          <option value="dine_in">La masă</option>
          <option value="takeaway">Ridicare</option>
          <option value="delivery">Livrare</option>
        </Select>
        <Input
          placeholder="Caută după cod, telefon sau nume…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="max-w-[280px]"
          aria-label="Căutare"
        />
        {openState?.has_hours ? (
          <Badge tone={openState.is_open ? 'success' : 'warning'} className="ml-auto">
            {openState.is_open
              ? `Deschis acum${openState.closes_at ? ` · până la ${openState.closes_at}` : ''}`
              : `Închis${openState.nextOpenLabel ? ` · deschidem ${openState.nextOpenLabel}` : ''}`}
          </Badge>
        ) : null}
      </Card>

      {orders === null ? (
        <div className="grid gap-3">
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
        </div>
      ) : orders.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Icon name="clock" size={26} />}
            title="Nicio comandă aici"
            description="Când cineva comandă din meniul public — de la masă, cu ridicare sau cu livrare — comanda apare aici în câteva secunde, cu sunet."
          />
        </Card>
      ) : (
        <ul className="grid gap-3">
          {orders.map((order) => {
            const next = NEXT_ACTION[order.status];
            const pending = order.status === 'pending';
            return (
              <Card
                as="li"
                key={order.id}
                className={pending ? 'border-warning/40 bg-warning-bg/30' : undefined}
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                    <span className="font-display text-[20px] leading-none text-ink-950">#{order.code}</span>
                    <Badge tone={STATUS_TONE[order.status]}>{STATUS_LABEL[order.status]}</Badge>
                    <Badge tone="neutral">{CHANNEL_LABEL[order.channel]}</Badge>
                    {order.table_label ? <Badge tone="info">{order.table_label}</Badge> : null}
                    <span className="text-caption text-ink-400">{formatDateTime(order.placed_at)}</span>
                    {order.eta_at && !['completed', 'rejected', 'cancelled'].includes(order.status) ? (
                      <span className="text-caption text-ink-400 nums">
                        promis{' '}
                        {new Date(order.eta_at).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    ) : null}
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setDetail(order)}>
                      Detalii
                    </Button>
                    <a
                      className="transition-ui inline-flex items-center gap-1 rounded-[var(--radius-md)] px-3 py-1.5 text-body-s font-medium text-ink-600 hover:bg-surface-2"
                      href={`/api/v1/orders/${order.id}/ticket`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Icon name="download" size={16} /> Bon
                    </a>
                    {session.can('order.update') && next ? (
                      <Button
                        size="sm"
                        loading={busy === order.id}
                        icon={<Icon name="check" size={16} />}
                        onClick={() => void act(order, next.action)}
                      >
                        {next.label}
                      </Button>
                    ) : null}
                    {session.can('order.update') && (order.status === 'pending' || order.status === 'accepted') ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-danger"
                        onClick={() => {
                          setRejecting(order);
                          setReason('');
                        }}
                      >
                        Refuză
                      </Button>
                    ) : null}
                    {session.can('order.update') && ['accepted', 'preparing', 'ready'].includes(order.status) ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => void act(order, 'cancel', 'Anulată de restaurant')}
                      >
                        Anulează
                      </Button>
                    ) : null}
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-x-6 gap-y-3">
                  <ul className="min-w-[240px] flex-1 divide-y divide-ink-100">
                    {(order.items ?? []).map((item, index) => (
                      <li key={index} className="flex items-baseline justify-between gap-3 py-1.5 text-body-s">
                        <span className="min-w-0">
                          <strong className="nums">{item.quantity}×</strong> {item.product_name}
                          {item.variant_name ? <span className="text-ink-400"> ({item.variant_name})</span> : null}
                          {item.note ? <em className="text-ink-400"> — {item.note}</em> : null}
                        </span>
                        <span className="nums whitespace-nowrap text-ink-600">
                          {formatCurrency(item.line_total_cents, order.currency)}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <div className="flex min-w-[220px] flex-col gap-1">
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="text-caption text-ink-400">Total</span>
                      <span className="font-display text-[18px] text-ink-950 nums">
                        {formatCurrency(order.total_cents, order.currency)}
                      </span>
                    </div>
                    {order.delivery_fee_cents > 0 ? (
                      <div className="flex items-baseline justify-between gap-3 text-caption text-ink-400">
                        <span>din care livrare</span>
                        <span className="nums">{formatCurrency(order.delivery_fee_cents, order.currency)}</span>
                      </div>
                    ) : null}
                    <div className="mt-1 text-caption text-ink-600">
                      {order.customer_name ? `${order.customer_name} · ` : ''}
                      {order.customer_phone}
                    </div>
                    {order.channel === 'delivery' && order.delivery_address ? (
                      <div className="text-caption text-ink-600">
                        {order.delivery_address}
                        {order.delivery_city ? `, ${order.delivery_city}` : ''}
                      </div>
                    ) : null}
                    {order.customer_note ? <div className="text-caption text-ink-600">Observații: {order.customer_note}</div> : null}
                    {order.reject_reason ? <div className="text-caption text-danger">Motiv: {order.reject_reason}</div> : null}
                  </div>
                </div>
              </Card>
            );
          })}
        </ul>
      )}

      <Modal
        open={Boolean(rejecting)}
        onClose={() => setRejecting(null)}
        title={rejecting ? `Refuză comanda #${rejecting.code}` : ''}
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setRejecting(null)}>
              Renunță
            </Button>
            <Button
              variant="danger"
              loading={busy === rejecting?.id}
              disabled={reason.trim().length < 3}
              onClick={() => {
                if (!rejecting) return;
                void act(rejecting, 'reject', reason.trim()).then(() => setRejecting(null));
              }}
            >
              Refuză comanda
            </Button>
          </div>
        }
      >
        <Field
          label="Motivul refuzului"
          required
          hint="Motivul apare clientului în pagina de urmărire a comenzii, așa că scrie-l concret (ex.: s-a terminat ingredientul)."
        >
          {(props) => (
            <Textarea
              {...props}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              rows={3}
              placeholder="Ex.: nu mai avem capacitatea necesară în acest interval"
            />
          )}
        </Field>
      </Modal>

      <Modal open={Boolean(detail)} onClose={() => setDetail(null)} title={detail ? `Comanda #${detail.code}` : ''}>
        {detail ? (
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={STATUS_TONE[detail.status]}>{STATUS_LABEL[detail.status]}</Badge>
              <Badge tone="neutral">{CHANNEL_LABEL[detail.channel]}</Badge>
              {detail.table_label ? <Badge tone="info">{detail.table_label}</Badge> : null}
              <span className="text-caption text-ink-400">plasată {formatDateTime(detail.placed_at)}</span>
            </div>

            <div className="text-body-s">
              <div className="text-caption text-ink-400">Client</div>
              <div>
                {detail.customer_name || '—'} · {detail.customer_phone}
              </div>
              {detail.channel === 'delivery' && detail.delivery_address ? (
                <div>
                  {detail.delivery_address}
                  {detail.delivery_city ? `, ${detail.delivery_city}` : ''}
                </div>
              ) : null}
              {detail.customer_note ? <div className="text-ink-600">Observații: {detail.customer_note}</div> : null}
            </div>

            <div>
              <div className="text-caption text-ink-400">Produse</div>
              <ul className="divide-y divide-ink-100">
                {(detail.items ?? []).map((item, index) => (
                  <li key={index} className="flex items-baseline justify-between gap-3 py-1.5 text-body-s">
                    <span>
                      <strong className="nums">{item.quantity}×</strong> {item.product_name}
                      {item.variant_name ? ` (${item.variant_name})` : ''}
                    </span>
                    <span className="nums">{formatCurrency(item.line_total_cents, detail.currency)}</span>
                  </li>
                ))}
              </ul>
            </div>

            {detail.status_history?.length ? (
              <div>
                <div className="text-caption text-ink-400">Cronologie</div>
                <ul className="flex flex-col gap-1 text-body-s">
                  {detail.status_history.map((entry, index) => (
                    <li key={index}>
                      <strong>{STATUS_LABEL[entry.status] ?? entry.status}</strong>
                      <span className="text-caption text-ink-400">
                        {' '}
                        · {formatDateTime(entry.at)}
                        {entry.note ? ` · ${entry.note}` : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <a
              className="transition-ui inline-flex w-fit items-center gap-1 rounded-[var(--radius-md)] px-3 py-1.5 text-body-s font-medium text-ink-600 hover:bg-surface-2"
              href={`/api/v1/orders/${detail.id}/ticket`}
              target="_blank"
              rel="noreferrer"
            >
              <Icon name="download" size={16} /> Deschide bonul de bucătărie
            </a>
          </div>
        ) : null}
      </Modal>

      {settingsFor !== null ? (
        <OrderingSettingsModal
          restaurantId={settingsFor}
          restaurants={restaurants}
          onRestaurant={setSettingsFor}
          onClose={() => setSettingsFor(null)}
        />
      ) : null}
    </div>
  );
}

/**
 * Spune exact ce se va întâmpla cu ce a scris omul: câte adrese valide are, care intră în listă și
 * ce text a fost ignorat. Fără asta, o adresă scrisă greșit ar fi înghițită tăcut la salvare, iar
 * cineva ar aștepta un email care nu are unde să ajungă.
 */
function EmailListFeedback({ text }: { text: string }) {
  const { valid, invalid } = parseEmails(text);
  const overLimit = valid.length > MAX_NOTIFY_EMAILS;

  if (!text.trim()) {
    return (
      <InlineAlert tone="info" title="Fără adrese proprii">
        Anunțul pe email merge la proprietarii și managerii contului. Adaugă adrese aici dacă vrei să
        ajungă în altă parte (de exemplu în bucătărie), fără să dea nimănui acces în dashboard.
      </InlineAlert>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm text-slate-600 dark:text-slate-300">
        {valid.length === 0
          ? 'Nicio adresă validă deocamdată.'
          : `${valid.length} ${valid.length === 1 ? 'adresă' : 'adrese'} din maximum ${MAX_NOTIFY_EMAILS}: ${valid.join(', ')}`}
      </p>
      {invalid.length > 0 ? (
        <InlineAlert tone="warning" title="Text ignorat">
          {invalid.join(', ')} {invalid.length === 1 ? 'nu este o adresă validă' : 'nu sunt adrese valide'} și nu
          {invalid.length === 1 ? ' va' : ' vor'} fi salvat{invalid.length === 1 ? 'ă' : 'e'}.
        </InlineAlert>
      ) : null}
      {overLimit ? (
        <InlineAlert tone="warning" title="Prea multe adrese">
          Peste maximum {MAX_NOTIFY_EMAILS} adrese, emailul nu se mai salvează. Șterge{' '}
          {valid.length - MAX_NOTIFY_EMAILS} dintre ele.
        </InlineAlert>
      ) : null}
    </div>
  );
}

/* ─────────────────────────── setările de comenzi ale restaurantului ─────────────────────── */

/**
 * Setările care se schimbă în timpul serviciului: oprești comenzile când bucătăria e plină,
 * ajustezi taxa de livrare sau pragul, mărești timpii de preparare când e aglomerat. Previzualizarea
 * intervalului promis se calculează exact ca pe server, ca managerul să vadă ce va citi clientul.
 */
function OrderingSettingsModal({
  restaurantId,
  restaurants,
  onRestaurant,
  onClose,
}: {
  restaurantId: string;
  restaurants: Array<{ id: string; name: string }>;
  onRestaurant: (id: string) => void;
  onClose: () => void;
}) {
  const toast = useToast();
  const [form, setForm] = useState<OrderingSettings | null>(null);
  /**
   * Textul adreselor este ținut separat de `form` deliberat: dacă ar fi derivat din listă
   * (`emails.join(', ')`), o virgulă scrisă la final ar dispărea imediat sub degetele
   * utilizatorului, iar editarea ar deveni imposibilă. Lista normalizată pleacă spre server la
   * fiecare tastă, textul rămâne exact cum a fost scris.
   */
  const [emailText, setEmailText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [zones, setZones] = useState<ZoneDraft[]>([]);
  const [priceTexts, setPriceTexts] = useState<string[]>([]);
  const [draft, setDraft] = useState<Array<[number, number]>>([]);
  const [tierName, setTierName] = useState('Tier 1');
  const [tierPrice, setTierPrice] = useState('10');
  const [mapOpen, setMapOpen] = useState(false);
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number; label: string } | null>(null);
  const zoneColors = ['#0f766e', '#b45309', '#1d4ed8', '#be123c', '#6d28d9'];

  useEffect(() => {
    let alive = true;
    setForm(null);
    if (!restaurantId) return;
    void (async () => {
      try {
        const settings = await api.get<OrderingSettings>(`/ordering/settings?restaurantId=${restaurantId}`);
        const loaded = await api.get<Array<{ name: string; priceCents: number; color: string; ring: Array<[number, number]> }>>(
          `/ordering/delivery-zones?restaurantId=${restaurantId}`,
        );
        if (alive) {
          setForm(settings);
          setEmailText((settings.notifyEmails ?? []).join(', '));
          setZones(loaded.map((zone) => ({ name: zone.name, priceCents: zone.priceCents, color: zone.color, ring: zone.ring })));
          setPriceTexts(loaded.map((zone) => (zone.priceCents / 100).toFixed(2)));
          setDraft([]);
          const center = await api.get<{ lat: number; lng: number; label: string }>('/ordering/delivery-map-center');
          if (alive) setMapCenter(center);
        }
      } catch (caught) {
        if (alive) setError(errorMessage(caught));
      }
    })();
    return () => {
      alive = false;
    };
  }, [restaurantId]);

  const { valid: validEmails } = parseEmails(emailText);

  const save = async () => {
    if (!form) return;
    /*
     * Trimiterea se blochează doar când lista VALIDĂ depășește limita: atunci serverul ar răspunde
     * 422, iar utilizatorul ar pierde timp încercând. Adresele scrise greșit sunt ignorate și
     * semnalate în interfață (`EmailListFeedback`), nu blochează salvarea celorlalte setări.
     */
    if (form.notifyNewOrder && validEmails.length > MAX_NOTIFY_EMAILS) {
      toast.error(
        `Cel mult ${MAX_NOTIFY_EMAILS} adrese de email`,
        `Ai ${validEmails.length} adrese valide. Șterge ${validEmails.length - MAX_NOTIFY_EMAILS} pentru a salva.`,
      );
      return;
    }
    setSaving(true);
    try {
      const updated = await api.patch<OrderingSettings>('/ordering/settings', form);
      const savedZones = await api.put<ZoneDraft[]>('/ordering/delivery-zones', {
        restaurantId,
        zones: zones.map((zone) => ({
          name: zone.name,
          priceCents: zone.priceCents,
          color: zone.color,
          ring: zone.ring,
        })),
      });
      setForm(updated);
      setZones(savedZones);
      setPriceTexts(savedZones.map((zone) => (zone.priceCents / 100).toFixed(2)));
      setDraft([]);
      toast.success('Setările au fost salvate', 'Meniurile publice folosesc noile valori imediat.');
    } catch (caught) {
      toast.error('Nu am putut salva setările', errorMessage(caught));
    } finally {
      setSaving(false);
    }
  };

  const number = (value: string) => Number(value.replace(/[^\d]/g, '') || 0);

  return (
    <Modal
      open
      onClose={onClose}
      title="Setări comenzi"
      description="Valorile se aplică imediat meniurilor publice care primesc comenzi."
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Închide
          </Button>
          <Button onClick={() => void save()} loading={saving} disabled={!form}>
            Salvează
          </Button>
        </div>
      }
    >
      {error && !form ? (
        <InlineAlert tone="danger" title="Nu am putut încărca setările">
          {error}
        </InlineAlert>
      ) : !form ? (
        <div className="grid gap-3">
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          {restaurants.length > 1 ? (
            <Field label="Locația">
              {(props) => (
                <Select {...props} value={restaurantId} onChange={(event) => onRestaurant(event.target.value)}>
                  {restaurants.map((restaurant) => (
                    <option key={restaurant.id} value={restaurant.id}>
                      {restaurant.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          ) : null}

          <div className="flex flex-col gap-3">
            <Switch
              checked={form.isEnabled}
              onChange={(value) => setForm({ ...form, isEnabled: value })}
              label="Comenzile online sunt activate"
              description="Oprește complet comenzile din meniurile publice: clienții văd meniul, dar nu pot comanda."
            />
            <Switch
              checked={form.acceptingOrders}
              onChange={(value) => setForm({ ...form, acceptingOrders: value })}
              label="Primim comenzi acum"
              description="Pauză rapidă în timpul serviciului: meniul rămâne vizibil, coșul se oprește pe loc."
            />
            <Switch
              checked={form.dineInEnabled}
              onChange={(value) => setForm({ ...form, dineInEnabled: value })}
              label="Comenzi la masă"
              description="Clienții scanează codul de pe masă și comandă fără să aștepte ospătarul."
            />
            <Switch
              checked={form.takeawayEnabled}
              onChange={(value) => setForm({ ...form, takeawayEnabled: value })}
              label="Ridicare personală"
              description="Clientul vine să ridice comanda; singurul lucru obligatoriu este numărul de telefon."
            />
            <Switch
              checked={form.deliveryEnabled}
              onChange={(value) => setForm({ ...form, deliveryEnabled: value })}
              label="Livrare la adresă"
              description="Comanda cere adresa, iar taxă de livrare și pragul minim se aplică automat."
            />
            <Switch
              checked={form.respectOpeningHours}
              onChange={(value) => setForm({ ...form, respectOpeningHours: value })}
              label="Comenzile respectă programul de funcționare"
              description="În afara orelor din pagina Locații, meniul rămâne vizibil, dar comenzile se opresc automat — nu mai depinde de cine apucă să apese pauză la închidere."
            />
          </div>

          {/*
            Notificările la comandă nouă. Anunțul în dashboard (clopoțel + alertă sonoră) există
            oricum — aici se decide doar dacă pleacă și un email, ca să nu stea nimeni cu ochii pe
            ecran în timpul serviciului. Implicit oprit: un produs care trimite emailuri necerute
            este spam, iar restaurantul trebuie să aleagă singur.
          */}
          <div className="rounded-xl border border-slate-200 p-4 dark:border-slate-700">
            <Switch
              checked={form.notifyNewOrder}
              onChange={(value) => setForm({ ...form, notifyNewOrder: value })}
              label="Anunță pe email când intră o comandă nouă"
              description="Notificarea în dashboard și alerta sonoră vin oricum. Emailul se trimite doar când îl pornești aici."
            />

            {form.notifyNewOrder ? (
              <div className="mt-4 flex flex-col gap-3">
                <Field
                  label="Către cine"
                  hint="Până la 5 adrese, separate prin virgulă. Lasă gol pentru a anunța proprietarii și managerii contului."
                >
                  {(props) => (
                    <Input
                      {...props}
                      value={emailText}
                      onChange={(event) => {
                        setEmailText(event.target.value);
                        setForm({ ...form, notifyEmails: parseEmails(event.target.value).valid });
                      }}
                      placeholder="ex. bucatar@restaurant.ro, manager@restaurant.ro"
                      autoComplete="off"
                    />
                  )}
                </Field>
                <EmailListFeedback text={emailText} />
              </div>
            ) : null}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Taxă de livrare" hint="În lei. Se folosește doar când nu există zone desenate. Dacă există zone, prețul vine din tierul adresei.">
              {(props) => (
                <Input
                  {...props}
                  value={(form.deliveryFeeCents / 100).toFixed(2)}
                  onChange={(event) => setForm({ ...form, deliveryFeeCents: Math.round(number(event.target.value) / 100) * 100 || 0 })}
                  inputMode="decimal"
                />
              )}
            </Field>
            <Field label="Comandă minimă" hint="Valoarea minimă a produselor, fără taxa de livrare.">
              {(props) => (
                <Input
                  {...props}
                  value={(form.minOrderCents / 100).toFixed(2)}
                  onChange={(event) => setForm({ ...form, minOrderCents: Math.round(number(event.target.value) / 100) * 100 })}
                  inputMode="decimal"
                />
              )}
            </Field>
            <Field label="Livrare gratuită de la" hint="Lasă gol dacă nu oferi livrare gratuită.">
              {(props) => (
                <Input
                  {...props}
                  value={form.freeDeliveryFromCents == null ? '' : (form.freeDeliveryFromCents / 100).toFixed(2)}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      freeDeliveryFromCents: event.target.value.trim() ? Math.round(number(event.target.value) / 100) * 100 : null,
                    })
                  }
                  inputMode="decimal"
                  placeholder="ex. 120,00"
                />
              )}
            </Field>
            <Field label="Fereastră de siguranță" hint="Minute adăugate la intervalul comunicat clientului.">
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  min={0}
                  max={120}
                  value={form.etaWindowMinutes}
                  onChange={(event) => setForm({ ...form, etaWindowMinutes: number(event.target.value) })}
                />
              )}
            </Field>
            <Field label="Pregătire de bază" hint="Cât durează, în medie, pregătirea unei comenzi (minute).">
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  min={0}
                  max={240}
                  value={form.prepBufferMinutes}
                  onChange={(event) => setForm({ ...form, prepBufferMinutes: number(event.target.value) })}
                />
              )}
            </Field>
            <Field label="Timp per comandă în bucătărie" hint="Cât adaugă fiecare comandă aflată deja în lucru (minute).">
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  min={0}
                  max={120}
                  value={form.kitchenQueueMinutes}
                  onChange={(event) => setForm({ ...form, kitchenQueueMinutes: number(event.target.value) })}
                />
              )}
            </Field>
            <Field label="Timp de livrare" hint="Minute de la ieșirea din bucătărie până la client.">
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  min={0}
                  max={240}
                  value={form.deliveryEtaMinutes}
                  onChange={(event) => setForm({ ...form, deliveryEtaMinutes: number(event.target.value) })}
                />
              )}
            </Field>
            <Field label="Timp până la ridicare" hint="Minute până când clientul poate veni după comandă.">
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  min={0}
                  max={240}
                  value={form.pickupEtaMinutes}
                  onChange={(event) => setForm({ ...form, pickupEtaMinutes: number(event.target.value) })}
                />
              )}
            </Field>
          </div>

          <Field label="Zone de livrare" hint="Text liber, afișat clientului în coș înainte de trimitere.">
            {(props) => (
              <Input
                {...props}
                value={form.deliveryAreas ?? ''}
                onChange={(event) => setForm({ ...form, deliveryAreas: event.target.value || null })}
                placeholder="ex. Alexandria (toate cartierele), Teleorman"
              />
            )}
          </Field>
          <div className="flex flex-col gap-3 rounded-[var(--radius-md)] border border-ink-100 bg-surface-2 p-4">
            <div>
              <p className="text-body-s font-medium text-ink-800">Zone de livrare pe hartă</p>
              <p className="text-caption text-ink-400">
                Clic pe hartă pentru colțurile zonei, apoi închide zona. Adresa clientului primește prețul tierului în care cade.
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Tier">
                {(props) => <Input {...props} value={tierName} onChange={(event) => setTierName(event.target.value)} placeholder="Tier 1" />}
              </Field>
              <Field label="Prețul tierului (lei)">
                {(props) => (
                  <Input
                    {...props}
                    value={tierPrice}
                    onChange={(event) => setTierPrice(event.target.value)}
                    inputMode="decimal"
                    placeholder="10"
                  />
                )}
              </Field>
            </div>
            <DeliveryZoneMap
              zones={zones}
              draft={draft}
              center={mapCenter}
              interactive={false}
              className="h-40"
            />
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-caption text-ink-400">
                {mapCenter ? `Previzualizare pe ${mapCenter.label}, orașul primului restaurant.` : 'Se încarcă orașul.'}
                {draft.length ? ` Contur: ${draft.length} puncte.` : ''}
              </p>
              <Button type="button" variant="secondary" size="sm" onClick={() => setMapOpen(true)}>
                Deschide harta
              </Button>
            </div>
            {zones.length ? (
              <ul className="flex flex-col gap-2">
                {zones.map((zone, index) => (
                  <li key={`${zone.color}-${index}`} className="flex flex-wrap items-center gap-2 text-body-s">
                    <span className="inline-block h-3 w-3 shrink-0 rounded-full" style={{ background: zone.color }} />
                    <Input
                      aria-label={`Numele zonei ${index + 1}`}
                      value={zone.name}
                      onChange={(event) =>
                        setZones((current) =>
                          current.map((item, itemIndex) => (itemIndex === index ? { ...item, name: event.target.value } : item)),
                        )
                      }
                      className="h-9 min-w-0 flex-1"
                    />
                    <Input
                      aria-label={`Prețul zonei ${index + 1}, în lei`}
                      value={priceTexts[index] ?? (zone.priceCents / 100).toFixed(2)}
                      inputMode="decimal"
                      onChange={(event) => {
                        const raw = event.target.value;
                        setPriceTexts((current) => current.map((item, itemIndex) => (itemIndex === index ? raw : item)));
                        const parsed = Number(raw.replace(',', '.'));
                        if (!Number.isFinite(parsed) || parsed < 0) return;
                        setZones((current) =>
                          current.map((item, itemIndex) => (itemIndex === index ? { ...item, priceCents: Math.round(parsed * 100) } : item)),
                        );
                      }}
                      className="h-9 w-28"
                    />
                    <span className="text-caption text-ink-400">lei</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setZones((current) => current.filter((_, item) => item !== index));
                        setPriceTexts((current) => current.filter((_, item) => item !== index));
                      }}
                    >
                      Șterge
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-caption text-ink-400">Nicio zonă. Fără zone desenate se folosește taxa unică de mai sus.</p>
            )}
            {mapOpen
              ? createPortal(
                  <div className="fixed inset-0 z-[80] flex items-center justify-center bg-ink-950/55 p-4">
                    <div className="flex h-[min(880px,calc(100dvh-2rem))] w-full max-w-5xl flex-col gap-4 rounded-[var(--radius-lg)] bg-surface p-4 shadow-[var(--shadow-elevated)]">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h2 className="font-display text-title text-ink-950">Zona de livrare</h2>
                          <p className="text-caption text-ink-400">
                            Clic pe hartă pentru colțuri, apoi închide zona. Hartă centrată pe {mapCenter?.label ?? 'orașul restaurantului'}.
                          </p>
                        </div>
                        <Button type="button" variant="ghost" size="sm" onClick={() => setMapOpen(false)}>
                          Închide
                        </Button>
                      </div>
                      <DeliveryZoneMap
                        zones={zones}
                        draft={draft}
                        center={mapCenter}
                        className="min-h-0 flex-1"
                        onAddPoint={(lng, lat) => setDraft((points) => [...points, [lng, lat]])}
                      />
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          disabled={draft.length < 3 || tierName.trim().length < 2}
                          onClick={() => {
                            const priceCents = Math.round(Number(tierPrice.replace(',', '.')) * 100);
                            setZones((current) => [
                              ...current,
                              {
                                name: tierName.trim(),
                                priceCents: Number.isFinite(priceCents) && priceCents >= 0 ? priceCents : 0,
                                color: zoneColors[current.length % zoneColors.length] ?? '#0f766e',
                                ring: draft,
                              },
                            ]);
                            setPriceTexts((current) => [...current, Number.isFinite(priceCents) ? (priceCents / 100).toFixed(2) : '0.00']);
                            setDraft([]);
                            setTierName(`Tier ${zones.length + 2}`);
                          }}
                        >
                          Închide zona
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          disabled={!draft.length}
                          onClick={() => setDraft((points) => points.slice(0, -1))}
                        >
                          Înapoi
                        </Button>
                        <Button type="button" variant="ghost" size="sm" disabled={!draft.length} onClick={() => setDraft([])}>
                          Renunță la contur
                        </Button>
                      </div>
                    </div>
                  </div>,
                  document.body,
                )
              : null}
          </div>

          <Field label="Observații pentru clienți" hint="Ex.: comenzile online se plătesc la livrare.">
            {(props) => (
              <Textarea
                {...props}
                value={form.notes ?? ''}
                onChange={(event) => setForm({ ...form, notes: event.target.value || null })}
                rows={2}
              />
            )}
          </Field>

          <InlineAlert tone="info" title="Intervalul promis clienților">
            Ridicare: în jur de {form.prepBufferMinutes + form.pickupEtaMinutes} min · Livrare: în jur de{' '}
            {form.prepBufferMinutes + form.deliveryEtaMinutes} min. Când coada din bucătărie crește, sistemul
            adaugă automat timpul fiecărei comenzi aflate în lucru ({form.kitchenQueueMinutes} min/comandă) și
            rotunjește la 5 minute.
          </InlineAlert>
        </div>
      )}
    </Modal>
  );
}
